import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  Tag,
  IconButton,
  Whisper,
  Popover,
  Dropdown,
  Message,
  useToaster,
  Pagination,
  Tooltip
} from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList, MdInfoOutline } from 'react-icons/md';
import { Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import EditDrawer from './EditDrawer';
import { fetchClaims } from '../../../services/claim.service';
import { getAdminUsers } from '../../../services/user.service';
import * as XLSX from 'xlsx';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = ({ type = '' }) => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const { data, loading } = useSelector(state => state.claim);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    userId: '',
    registrationNumber: '',
    status: [],
    type: type === 'Combined' ? '' : type // Initialize type filter as empty for Combined to avoid filtering out data
  });
  const showTypeColumn = !type || type === 'Combined';
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getAdminUsers());
    dispatch(fetchClaims(type));
  }, [dispatch, type]);

  const applyFilters = (dataToFilter, searchTerm, currentFilters) => {
    let filtered = [...dataToFilter];

    // Only apply type filter if type is not empty and not 'Combined'
    if (currentFilters.type && currentFilters.type !== 'Combined') {
      filtered = filtered.filter(item => item.type === currentFilters.type);
    } else if (type === 'Combined') {
      // For Combined, only show Home Office and Third Party Site
      filtered = filtered.filter(item => ['remoteWork', 'thirdPartySite'].includes(item.type));
    }

    if (searchTerm.trim()) {
      const searchStr = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        item =>
          `${item.idSender?.firstname || ''} ${item.idSender?.lastname || ''}`
            .toLowerCase()
            .includes(searchStr) ||
          (item.idSender?.registrationNumber || '').toLowerCase().includes(searchStr) ||
          (item.type || '').toLowerCase().includes(searchStr) ||
          (item.CommentUser || '').toLowerCase().includes(searchStr) ||
          (item.status || '').toLowerCase().includes(searchStr)
      );
    }

    if (currentFilters.userId) {
      filtered = filtered.filter(item => item.idSender?._id === currentFilters.userId);
    }

    if (currentFilters.registrationNumber) {
      filtered = filtered.filter(item =>
        (item.idSender?.registrationNumber || '')
          .toLowerCase()
          .includes(currentFilters.registrationNumber.toLowerCase())
      );
    }

    if (currentFilters.status && currentFilters.status.length > 0) {
      filtered = filtered.filter(item =>
        currentFilters.status.includes(item.status?.toLowerCase())
      );
    }

    return filtered;
  };

  useEffect(() => {
    const newFilteredData = applyFilters(data, searchKeyword, filters);
    setFilteredData(newFilteredData);
  }, [data, searchKeyword, filters]);

  const typeChange = {
    remoteWork: 'Home Office',
    thirdPartySite: 'Third Party Site'
  };

  const handleSortColumn = (sortColumn, sortType) => {
    setSortColumn(sortColumn);
    setSortType(sortType);
  };

  const handleSearch = searchTerm => {
    setSearchKeyword(searchTerm);
    setPage(1);
  };

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
    // Only fetch claims if the type filter has changed and is not Combined
    if (newFilters.type !== 'Combined') {
      dispatch(fetchClaims(newFilters.type || type));
    }
    toaster.push(
      <Message type="success" closable>
        {t('Filters applied successfully')}
      </Message>
    );
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      registrationNumber: '',
      status: [],
      type: type === 'Combined' ? '' : type
    });
    setPage(1);
    dispatch(fetchClaims(type));
    toaster.push(
      <Message type="info" closable>
        Filters have been reset
      </Message>
    );
  };

  const getStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'processing':
        return 'green';
      case 'closed':
        return 'red';
      default:
        return 'gray';
    }
  };

  const handleView = (rowData, e) => {
    e.stopPropagation();
    setSelectedClaim(rowData);
    setShowEditDrawer(true);
  };

  const renderMenu = ({ onClose, left, top, className }, ref, rowData) => {
    const handleSelect = eventKey => {
      onClose();
      if (eventKey === 'view') {
        handleView(rowData);
      }
    };

    return (
      <Popover ref={ref} className={className} style={{ left, top }} full>
        <Dropdown.Menu onSelect={handleSelect}>
          <Dropdown.Item eventKey="view">{t('View Details')}</Dropdown.Item>
        </Dropdown.Menu>
      </Popover>
    );
  };

  const ActionCell = ({ rowData, ...props }) => {
    return (
      <Cell {...props} className="link-group">
        <Stack spacing={6}>
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>{t('View Details')}</Tooltip>}>
            <IconButton
              size="xs"
              icon={<Icon as={MdInfoOutline} />}
              onClick={e => handleView(rowData, e)}
            />
          </Whisper>
        </Stack>
      </Cell>
    );
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const supportsToExport = filteredData.map(item => {
      return {
        'Full Name': `${item.idSender?.firstname || ''} ${item.idSender?.lastname || ''}`,
        'Registration Number': item.idSender?.registrationNumber || '',
        Type: item.type || '',
        Comment: item.CommentUser || '',
        Status: item.status || '',
        'Created At': new Date(item.createdAt).toLocaleString()
      };
    });

    downloadExcel(supportsToExport, type ? `${type} Support` : 'Support');
  };

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </Stack>

        <Stack spacing={6}>
          <Button appearance="primary" onClick={() => setShowFilterDrawer(true)}>
            <Icon as={MdFilterList} style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={handleExport}>
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>
        </Stack>
      </Stack>
      <>
        <Table
          height={Math.max(getHeight(window) - 200, 400)}
          data={filteredData}
          sortColumn={sortColumn}
          sortType={sortType}
          onSortColumn={handleSortColumn}
          loading={loading}
        >
          <Column flexGrow={4}>
            <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
            <Cell>
              {rowData =>
                `${rowData.idSender?.firstname || ''} ${rowData.idSender?.lastname || ''}`
              }
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Registration Number')}</HeaderCell>
            <Cell>{rowData => rowData.idSender?.registrationNumber}</Cell>
          </Column>

          {showTypeColumn && (
            <Column flexGrow={3}>
              <HeaderCell>{t('Type')}</HeaderCell>
              <Cell>{rowData => typeChange[rowData.type] || rowData.type}</Cell>
            </Column>
          )}

          <Column flexGrow={5}>
            <HeaderCell>{t('Comment')}</HeaderCell>
            <Cell dataKey="CommentUser" />
          </Column>

          <Column flexGrow={2}>
            <HeaderCell>{t('Status')}</HeaderCell>
            <Cell>
              {rowData => <Tag color={getStatusColor(rowData.status)}>{t(rowData.status)}</Tag>}
            </Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Created at')}</HeaderCell>
            <Cell>{rowData => new Date(rowData.createdAt).toLocaleString()}</Cell>
          </Column>

          <Column width={70} fixed="right">
            <HeaderCell>{t('Action')}</HeaderCell>
            <ActionCell />
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={filteredData.length}
            limitOptions={[10, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </>

      <DrawerView
        open={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={users}
        usersLoading={usersLoading}
        toaster={toaster}
        type={type}
      />

      {selectedClaim && (
        <EditDrawer
          open={showEditDrawer}
          claim={selectedClaim}
          type={type}
          onClose={() => {
            setShowEditDrawer(false);
            setSelectedClaim(null);
          }}
        />
      )}
    </>
  );
};

export default DataTable;
